import { FC, useState } from 'react';

import { withRoleAccess } from '@app/routes';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from '@mui/icons-material/Edit';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Tooltip } from '@mui/material';
import { useModal } from '@shared/components/Modal';
import { useTableContext } from '@shared/components/Table';

import { ITableActionsConfig, IButtonConfig } from '../model/types';

import '../table-action-btns.css';

const Button = (props: React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  return <button {...props}>{props.children}</button>;
};

interface IProps {
  config: ITableActionsConfig;
}

export const TableActionBtns: FC<IProps> = ({ config }) => {
  const { selectedIds, setSelectedIds } = useTableContext();
  const { openModal } = useModal();
  const [openTooltips, setOpenTooltips] = useState<Record<string, boolean>>({});

  const buttonsConfig: IButtonConfig[] = [
    { key: 'edit', Icon: EditIcon, tooltip: 'Edit' },
    { key: 'edit-settings', Icon: SettingsIcon, tooltip: 'Edit settings' },
    { key: 'view', Icon: VisibilityIcon, tooltip: 'View' },
    { key: 'view-edit', Icon: LocalOfferIcon, tooltip: 'View & Edit' },
    { key: 'approve', Icon: DoneIcon, tooltip: 'Approve' },
    { key: 'refund', Icon: MoneyOffIcon, tooltip: 'Refund' },
    { key: 'reject', Icon: ClearIcon, tooltip: 'Reject' },
    { key: 'delete', Icon: ClearIcon, tooltip: 'Delete' },
    { key: 'add', Icon: AddCircleIcon, tooltip: 'Add' },
  ];

  const handleTooltipOpen = (key: string) => {
    setOpenTooltips(prev => ({ ...prev, [key]: true }));
  };

  const handleTooltipClose = (key: string) => {
    setOpenTooltips(prev => ({ ...prev, [key]: false }));
  };

  const renderButtons = Object.entries(config).map(([key, action]) => {
    const { popupElem, popupSize, isEnabled, isVisible } = action;
    const Icon = buttonsConfig.find(btn => btn.key === key)?.Icon;

    if (isVisible) {
      const ActionBtn = action.allowedRoles?.length
        ? withRoleAccess(Button, action.allowedRoles)
        : Button;

      return (
        <Tooltip
          key={key}
          title={buttonsConfig.find(btn => btn.key === key)?.tooltip}
          open={openTooltips[key] || false}
          onOpen={() => handleTooltipOpen(key)}
          onClose={() => handleTooltipClose(key)}
        >
          <span>
            <ActionBtn
              className={`btn-${key} ${isEnabled ? 'is-enabled' : 'is-disabled'}`}
              onClick={() => {
                if (isEnabled) {
                  handleTooltipClose(key); // Close tooltip before opening modal
                  openModal(popupElem, popupSize, { selectedIds, setSelectedIds });
                }
              }}
            >
              <Icon />
            </ActionBtn>
          </span>
        </Tooltip>
      );
    }

    return null;
  });

  return <div className='action-btns'>{renderButtons}</div>;
};
